import ConfirmDialog from '@components/Shared/ConfirmDialog';
import {
  Alert, Card, CardContent, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography
} from '@mui/material';
import { useGetConfigQuery, useUpdateConfigMutation } from '@store/api/rootEndpoints';
import { useEffect, useState } from 'react';

const RETENTION_CHOICES = [
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 365, label: '12 months (default)' },
  { days: 730, label: '24 months' },
];

/**
 * What this server writes down about the devices using it.
 *
 * Every row on this screen is a decision somebody has to be able to point at during a review, which
 * is why the two fixed behaviours are shown rather than hidden. **They are deliberately not
 * switches**: a setting that can be turned on is a setting somebody turns on to debug something on
 * a Friday, and the commitment made to the customer has no debug exception.
 */
const LoggingCard = () => {
  const { data: config } = useGetConfigQuery();
  const [updateConfig, { isLoading }] = useUpdateConfigMutation();

  const [retentionDays, setRetentionDays] = useState(365);
  const [recordClientIp, setRecordClientIp] = useState(false);
  // A shorter period is the one control on this screen that destroys something, so it waits.
  const [shortening, setShortening] = useState<number>();

  useEffect(() => {
    if (!config?.logging) return;

    setRetentionDays(config.logging.retentionDays);
    setRecordClientIp(config.logging.recordClientIp);
  }, [config]);

  // Saved on change rather than behind a button. There are two controls and a Save button on a
  // two-field screen is a step that only exists to be forgotten. Turning the IP off again, or
  // lengthening the period, undoes itself; shortening the period does not, which is why that one
  // goes through a confirmation instead.
  const save = async (next: { retentionDays?: number, recordClientIp?: boolean }) => {
    if (!config) return;

    await updateConfig({
      auth: config.auth,
      logging: {
        retentionDays: next.retentionDays ?? retentionDays,
        recordClientIp: next.recordClientIp ?? recordClientIp,
      },
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant='h6' gutterBottom>Logging</Typography>

        <Typography variant='body2' color='text.secondary'>
          Grants, reclaims, releases and refusals are recorded. Heartbeats are not: every client
          calls back every fifteen minutes and would bury everything else.
        </Typography>

        {/* The space above is the fields', not the text's: `index.css` zeroes every paragraph's
            bottom margin, and without it the retention field's label sat on the sentence above. */}
        <Stack spacing={3} sx={{ maxWidth: 560, mt: 3 }}>
          <TextField
            select
            label='Keep activation logs for'
            value={retentionDays}
            disabled={isLoading}
            onChange={(event) => {
              const days = Number(event.target.value);

              // The select keeps showing the period in force until the deletion is agreed to.
              if (days < retentionDays) {
                setShortening(days);

                return;
              }

              setRetentionDays(days);
              void save({ retentionDays: days });
            }}
            helperText='The default is what was agreed with the customer, not a preference. Shown here because the reviewer asking about it is usually standing behind you.'
          >
            {RETENTION_CHOICES.map(choice => (
              <MenuItem key={choice.days} value={choice.days}>{choice.label}</MenuItem>
            ))}
          </TextField>

          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={recordClientIp}
                  disabled={isLoading}
                  onChange={(event) => {
                    setRecordClientIp(event.target.checked);
                    void save({ recordClientIp: event.target.checked });
                  }}
                />
              }
              label='Record the client IP address'
            />
            <Typography variant='caption' color='text.secondary'>
              Off by default. On a plant network an address usually identifies a workstation, and
              therefore a person. Turning it on is your decision to make and to defend; turning it
              off again does not remove what has already been written.
            </Typography>
          </Stack>

          <Alert severity='info' icon={false}>
            <Typography variant='body2' sx={{ mb: 1 }}><strong>Always, and not configurable:</strong></Typography>
            <Typography variant='body2' sx={{ mb: 0.5 }}>
              The <strong>device identifier</strong> is stored as a salted hash. The salt is
              generated here and never leaves this server, so rows can be compared within this site
              and mean nothing outside it.
            </Typography>
            <Typography variant='body2' sx={{ mb: 0.5 }}>
              The <strong>device name</strong> is never recorded. On iPads it is usually a person&apos;s
              name.
            </Typography>
            <Typography variant='body2'>
              The <strong>seat usage</strong> of each license, counted per application and day with
              nothing about any device, is kept without a time limit. The period above does not
              shorten it.
            </Typography>
          </Alert>
        </Stack>
      </CardContent>

      {shortening !== undefined && (
        <ConfirmDialog
          title='Shorten how long logs are kept?'
          confirmLabel='Shorten retention'
          confirmColor='warning'
          onClose={() => setShortening(undefined)}
          onConfirm={async () => {
            setRetentionDays(shortening);
            await save({ retentionDays: shortening });
          }}
        >
          Activation logs older than{' '}
          {RETENTION_CHOICES.find(choice => choice.days === shortening)?.label ?? `${shortening} days`}
          {' '}are deleted within about a minute of saving this, and this server cannot bring them
          back. Complete months already written to the archive are not touched; they keep their
          own two-year window. Anything since the last archived month is gone for good, and so is
          what a reviewer could have asked to see.
        </ConfirmDialog>
      )}
    </Card>
  );
};

export default LoggingCard;
